// Manually authored blog posts (group 2). No automation, no generated imagery:
// every image below is an asset that already exists in this project.

import type { BlogPost } from "./blogPosts";

import blogHero1 from "@/assets/blog/blog-1.jpg.asset.json";
import solarInstall from "@/assets/mckay/solar-install.webp.asset.json";
import blogHero5 from "@/assets/blog/blog-5.png.asset.json";
import roofingService from "@/assets/mckay/roofing-service.webp.asset.json";
import blogHero6 from "@/assets/blog/blog-6.png.asset.json";
import singlePlyRoofing from "@/assets/service-heroes/single-ply-roofing.jpg.asset.json";

export const group2Posts: BlogPost[] = [
  {
    slug: "tpo-roof-solar-installation-san-diego",
    title: "Gravel Roof Tear-Off, TPO + Solar in SD | McKay",
    h1: "How do you convert an old gravel roof into a TPO roof that carries solar in San Diego?",
    metaDescription:
      "A full gravel built-up roof tear-off, new 60 mil TPO membrane, and rooftop solar install in San Diego. Materials, flashing details, and why the order of operations matters.",
    keywords:
      "TPO roof solar installation San Diego, gravel roof tear off, 60 mil TPO membrane, commercial roof solar, flat roof solar mounting, San Diego commercial roofing",
    category: "Projects",
    datePublished: "2026-05-03",
    dateModified: "2026-09-09",
    readMinutes: 8,
    hero: {
      src: blogHero1.url,
      alt: "Crew tearing off an old gravel built-up roof before installing a new TPO membrane and solar array in San Diego",
      caption: "The old gravel roof came off in dumpster after dumpster before a single roll of TPO went down.",
    },
    intro: [
      "This San Diego property came to us with a problem that shows up on a lot of older flat roofs: a built-up gravel roof that had been patched more times than anyone could remember, sitting under a solar array the owner wanted to keep or expand. You cannot mount solar racking on loose gravel and asphalt and expect it to perform for twenty years. Something underneath has to be solid, sealed, and rated to carry the load and the foot traffic that comes with panel installation and future maintenance.",
      "The scope ended up being two projects stacked into one: a full tear-off of the old gravel roof down to the deck, followed by a new 60 mil TPO membrane system, followed by a rooftop solar installation on top of that new membrane. Doing it in that order, instead of patching around the panels, is the only way to get a roof and a solar system that are both actually warrantied.",
      "We are walking through this one in detail because it is the project people ask about most: can you really put solar on a flat roof, and does the roof underneath actually matter as much as we say it does. Short answer: yes, and yes. Here is what the job looked like from tear-off to interconnection, with the materials and numbers we used.",
    ],
    tldr: [
      "The existing gravel built-up roof was torn off completely rather than roofed over, exposing and repairing sections of soft decking before any new membrane went down.",
      "The new roof is a 60 mil TPO single-ply system, mechanically attached, with reinforced flashing at every penetration and parapet.",
      "Solar racking was set on the new TPO using non-penetrating ballasted mounts and sealed, flashed attachment points where required, keeping the roof's warranty intact.",
      "Sequencing mattered: roof first, solar second. Roofing over an old system or working around an existing array almost always costs more in the long run than doing the roof right the first time.",
    ],
    sections: [
      {
        id: "why-tear-off-a-gravel-roof",
        h2: "Why tear off a gravel roof instead of roofing over it?",
        blocks: [
          {
            type: "p",
            text: "Gravel built-up roofs (BUR) were standard on San Diego commercial and multi-family buildings for decades: layers of asphalt-saturated felt mopped together and topped with a flood coat of asphalt and a layer of loose gravel for UV protection. They work, until they do not. By the time a BUR roof is 25 to 30 years old, the felts underneath the gravel are usually brittle, the flood coat has alligatored, and water has found at least a few ways in around old vents and drains.",
          },
          {
            type: "p",
            text: "You can roof over a BUR in some cases, but not when the plan is to add solar. Loose gravel does not give racking a stable base, and roofing over a wet or damaged system just seals the moisture in. We tore this one off entirely: gravel, flood coat, felt layers, all of it, down to the plywood deck.",
          },
          {
            type: "ul",
            items: [
              "**Tear-off exposes the deck.** You cannot know the true condition of the sheathing until the old roof is off it.",
              "**It resets the warranty clock.** A new TPO system over an old, unknown substrate is a much weaker warranty than TPO over inspected, repaired decking.",
              "**It is the only way to get a flat, uniform surface for a solar racking layout,** which matters for panel alignment and for keeping water moving to the drains instead of pooling around mounts.",
            ],
          },
          {
            type: "p",
            text: "Full details on how we scope a project like this live on our [commercial roofing](/services/commercial-roofing) page, and on what a tear-off and inspection actually involve on our [roof inspection](/services/roof-inspection) service.",
          },
        ],
        image: {
          src: solarInstall.url,
          alt: "Rooftop solar panels mounted on a finished TPO membrane roof in San Diego",
          caption: "Solar racking sits on a flat, sealed TPO field instead of loose gravel and patched asphalt.",
        },
      },
      {
        id: "what-we-found-under-the-gravel",
        h2: "What did the tear-off reveal underneath the old roof?",
        blocks: [
          {
            type: "p",
            text: "Once the gravel and felt layers were stripped, the deck told the story we expected: mostly sound plywood, with soft, delaminated sections around two roof drains and along a parapet wall where flashing had failed years earlier and let water track sideways under the membrane.",
          },
          {
            type: "h3", text: "Deck condition, section by section",
          },
          {
            type: "ol",
            items: [
              "Field areas away from drains and walls: firm, dry decking, no replacement needed.",
              "Drain areas: soft plywood in roughly a four foot radius around each drain, replaced with new CDX sheathing tied into existing framing.",
              "Parapet wall perimeter: approximately 40 linear feet of decking replaced where the old counterflashing had let water in behind the wall for an extended period.",
            ],
          },
          {
            type: "callout",
            title: "Why this step cannot be skipped",
            text: "If we had roofed over that soft decking instead of cutting it out, the new TPO membrane would have been fastened into rotten wood. Mechanical fasteners need sound substrate to hold their pull-out rating, and that rating is what keeps a membrane attached in wind.",
          },
          {
            type: "p",
            text: "This is also the point in a project where an owner can get an accurate number instead of an estimate padded for unknowns. We do not guess at decking; we open it up, price what we find, and show it before closing it back in.",
          },
        ],
      },
      {
        id: "the-tpo-system",
        h2: "What TPO system went down, and why 60 mil?",
        blocks: [
          {
            type: "p",
            text: "TPO (thermoplastic polyolefin) is a single-ply membrane that has become the standard for flat and low-slope commercial roofs in Southern California. It reflects heat, resists UV without a coating, and its seams are heat-welded rather than glued, which gives a much stronger, more consistent seal than older systems.",
          },
          {
            type: "p",
            text: "We specified a 60 mil membrane rather than the thinner 45 mil option that some contractors use to save on material cost. On a roof that is going to carry a solar array and the foot traffic that comes with installing and servicing it, the extra thickness buys real puncture and wear resistance where it matters most.",
          },
          {
            type: "ul",
            items: [
              "**Membrane:** 60 mil, white reflective TPO, mechanically attached over new insulation board.",
              "**Insulation:** polyisocyanurate board, tapered where needed at drains to correct minor low spots and keep water moving.",
              "**Seams:** hot-air welded, not adhesive-taped, then probed to confirm weld integrity before the next roll went down.",
              "**Flashing:** reinforced TPO flashing at every penetration, drain, and the full parapet perimeter, with new sheet metal counterflashing over the top.",
              "**Warranty:** manufacturer material and workmanship coverage, more detail on what that includes is on our [TPO roofing](/services/tpo-roofing) page.",
            ],
          },
          {
            type: "quote",
            text: "A membrane is only as good as its seams and its flashing. The flat field almost never fails first; the edges and penetrations do.",
            cite: "McKay Roofing & Solar field notes",
          },
        ],
        image: {
          src: roofingService.url,
          alt: "New white TPO membrane roof being installed with heat-welded seams on a San Diego commercial building",
          caption: "Heat-welded seams on the new 60 mil TPO field, probed and checked before moving to the next section.",
        },
      },
      {
        id: "sequencing-roof-then-solar",
        h2: "Why install the roof completely before the solar array?",
        blocks: [
          {
            type: "p",
            text: "It sounds obvious, but a lot of solar installers will mount panels on whatever roof is there, because roofing is not their trade. That approach works fine on a roof with 15 healthy years left. It is a bad plan on a roof that is already near the end of its service life, because pulling panels off to redo the roof in five years costs more than doing the roof first would have.",
          },
          {
            type: "p",
            text: "Because McKay handles both trades, we sequenced this job as one project instead of two separate contracts handed to two separate crews on two separate timelines. Tear-off and new TPO membrane came first and were fully cured and inspected. Only then did the racking layout get chalked out and the array installed.",
          },
          {
            type: "ol",
            items: [
              "Tear-off and deck repair.",
              "New insulation board and 60 mil TPO membrane, seams welded and tested.",
              "Membrane inspection and sign-off before any racking touches the roof.",
              "Solar layout marked out to avoid drains, parapet edges, and rooftop equipment.",
              "Racking installed, panels mounted, electrical run and connected, system commissioned.",
            ],
          },
          {
            type: "p",
            text: "This is the same reason we push customers considering solar to get a roof condition check first. Our [solar installation](/services/solar-installation) team and our roofing crews scope these jointly so the customer is not paying twice for the same square footage of labor.",
          },
        ],
      },
      {
        id: "mounting-solar-without-voiding-the-roof",
        h2: "How do you mount solar on a flat roof without voiding the roof warranty?",
        blocks: [
          {
            type: "p",
            text: "This is the detail most people do not think about until they are already dealing with a leak: every bolt through a membrane is a place water can get in, and most TPO manufacturers will not warranty a roof if the penetrations were not flashed to their specification.",
          },
          {
            type: "p",
            text: "On this project the racking system used a mix of ballasted, non-penetrating mounts across most of the field, which rely on weight rather than fasteners to resist wind uplift, and a smaller number of mechanically attached mounts at the array's edges where wind load calculations required them. Every mechanical attachment point got a fully adhered TPO flashing boot, hot-air welded to the field membrane, with a sealed cover plate over the fastener itself.",
          },
          {
            type: "ul",
            items: [
              "Ballasted mounts protect the membrane by eliminating unnecessary penetrations across most of the array footprint.",
              "Where penetrations were required, each one was flashed and welded before the mount was ever loaded with a panel.",
              "The finished attachment points were documented with photos, which we keep on file and can provide to the manufacturer if a warranty question ever comes up.",
            ],
          },
          {
            type: "callout",
            title: "Ask this before anyone touches your roof for solar",
            text: "Ask any solar company how they are attaching to your specific roof type, and ask whether that method is on their racking manufacturer's approved list for your membrane. If they cannot answer specifically, they have not done this enough times.",
          },
        ],
        image: {
          src: singlePlyRoofing.url,
          alt: "Close-up of a flashed and sealed solar mount penetration on a TPO single-ply roof",
          caption: "A properly flashed mounting point: welded boot, sealed cover, no exposed fastener.",
        },
      },
      {
        id: "results-and-timeline",
        h2: "How long did the project take, and what does the owner get out of it?",
        blocks: [
          {
            type: "p",
            text: "Tear-off, deck repair, and the new TPO membrane took roughly two weeks depending on weather and drying time for the deck repairs. Solar installation and electrical work followed over the next week, with interconnection and utility approval adding additional time outside of our crew's control.",
          },
          {
            type: "p",
            text: "The owner ended up with a roof rated for 20 to 30 years, a solar array mounted correctly on that roof instead of on the old failing system, and paperwork that ties both together: roof warranty documentation and racking attachment records in one file. That matters if the property ever changes hands, since a buyer's inspector will ask exactly the questions we answered as we went.",
          },
          {
            type: "p",
            text: "You can see more completed projects like this one in our [project gallery](/gallery), and if you are weighing a similar tear-off, replacement, or solar combination project, our [contact](/contact) page is the fastest way to get a crew out to look at the roof in person.",
          },
          {
            type: "p",
            text: "For general background on TPO membrane performance and testing, the [Environmental Protection Agency's ENERGY STAR roof products program](https://www.energystar.gov/) and the [Cool Roof Rating Council](https://coolroofs.org/) both publish independent reflectance and durability data worth reviewing before choosing a membrane color and thickness.",
          },
        ],
      },
    ],
    faqs: [
      {
        q: "Can you install solar on any flat commercial roof?",
        a: "Only if the roof is structurally sound and has enough service life left to outlast the solar array's mounting warranty. If it does not, we recommend a tear-off and new membrane first, which is what happened on this project.",
      },
      {
        q: "Is TPO better than a gravel built-up roof for solar?",
        a: "Yes, for solar specifically. TPO gives a flat, weldable, inspectable surface for racking layout and flashing. Loose gravel cannot support a stable racking base without a membrane underneath it anyway.",
      },
      {
        q: "Does adding solar void a new TPO roof warranty?",
        a: "It can, if the racking is attached incorrectly. Ballasted, non-penetrating mounts and properly welded flashing boots at any required penetrations keep the manufacturer's roof warranty intact.",
      },
      {
        q: "How thick should a TPO membrane be under solar panels?",
        a: "We generally recommend 60 mil rather than 45 mil under solar because of the added foot traffic and equipment weight involved in installing and servicing an array over the roof's lifespan.",
      },
      {
        q: "How long does a combined roof and solar project take?",
        a: "This project took about three weeks total for tear-off, decking repair, membrane installation, and solar racking and panel installation, not including utility interconnection approval timelines.",
      },
    ],
    cta: {
      title: "Planning a roof and solar project together?",
      text: "We handle both the roofing and the solar racking under one crew, so nothing gets mounted on a roof that will not carry it. Tell us about your building and we will scope it properly.",
    },
    ctaType: "solar",
  },
  {
    slug: "san-diego-roof-replacement-good-life-property",
    title: "Roof Replacement for Good Life Property | McKay",
    h1: "What did a full roof replacement look like for Good Life Property Management in San Diego?",
    metaDescription:
      "A San Diego roof replacement for Good Life Property Management: CertainTeed shingle installation on the sloped sections and a silicone coating over the existing flat torch-down roof.",
    keywords:
      "San Diego roof replacement, CertainTeed shingle installation, silicone roof coating, torch down roof, property management roofing, multi-family roof replacement",
    category: "Projects",
    datePublished: "2026-03-13",
    dateModified: "2026-09-09",
    readMinutes: 7,
    hero: {
      src: blogHero5.url,
      alt: "Completed CertainTeed shingle roof replacement on a San Diego property managed by Good Life Property Management",
      caption: "New CertainTeed shingles on the sloped sections, finished and cleaned up for Good Life Property Management.",
    },
    intro: [
      "Property managers do not have the luxury of guessing on a roof. A leak on a managed property turns into tenant calls, and a roof project that runs long or over budget turns into a conversation with an owner who was expecting a number, not a range. That is the environment we worked in on this project for Good Life Property Management in San Diego, where the building had two different roof types that needed two different fixes on the same visit.",
      "The property's main sloped sections were on an aging shingle roof that had reached the point where patching no longer made sense, and a separate flat section covered in an older torch-down modified bitumen system that still had usable life left in it but needed protection before it started failing. Rather than treat those as two disconnected jobs, we scoped them together: full shingle replacement on the sloped roof, and a silicone coating system over the existing flat torch-down roof.",
      "This is a common combination on multi-family and commercial properties in San Diego, where additions, garages, and covered walkways often carry a different roof type than the main structure. Here is exactly what went into each part of the job, the materials used, and why the flat roof got a coating instead of a full tear-off.",
    ],
    tldr: [
      "The sloped roof sections got a full tear-off and replacement with CertainTeed architectural shingles, including new underlayment and flashing throughout.",
      "The existing flat torch-down roof was not torn off; it was cleaned, repaired at weak points, and coated with a silicone roof coating system to extend its life.",
      "Coating an existing flat roof instead of replacing it saved the client a significant tear-off and disposal cost while still addressing the roof's remaining problem areas.",
      "Both scopes were coordinated on one schedule so the property had one mobilization, one crew, and one point of contact instead of two separate vendors.",
    ],
    sections: [
      {
        id: "why-a-property-manager-called",
        h2: "Why did this property need two different roofing approaches?",
        blocks: [
          {
            type: "p",
            text: "Good Life Property Management came to us with a fairly typical multi-family layout: pitched roof over the main living areas, and a flat, low-slope roof over an attached section covered in an older torch-down modified bitumen membrane. The shingles were showing granule loss, curling at the edges, and had a few active leaks reported by tenants. The flat torch-down roof was not leaking yet, but it had visible cracking and a few blistered areas that flagged it as a near-term problem.",
          },
          {
            type: "p",
            text: "Two different roof types age differently and fail differently, so the fix for one was not automatically the fix for the other. Replacing both from scratch would have meant a bigger budget and a longer project than the property actually needed at this point in each roof's life.",
          },
          {
            type: "p",
            text: "This is the kind of assessment we run on every [roof inspection](/services/roof-inspection): figure out what each section of roof actually needs, not just quote a blanket number for the whole building.",
          },
        ],
        image: {
          src: roofingService.url,
          alt: "Roofing crew assessing a multi-family San Diego property with both sloped shingle and flat torch-down roof sections",
          caption: "Two roof types, two different scopes: shingle replacement and a coating system on the same visit.",
        },
      },
      {
        id: "the-shingle-replacement",
        h2: "What went into the CertainTeed shingle replacement?",
        blocks: [
          {
            type: "p",
            text: "The sloped sections came all the way off down to the decking. We inspected the sheathing as we went, replacing a small number of sheets where moisture had gotten in around old flashing, then installed a full new roofing system rather than layering over what was there.",
          },
          {
            type: "ul",
            items: [
              "**Shingle:** CertainTeed architectural laminate shingles, chosen for wind resistance and a manufacturer warranty that holds up under San Diego's UV exposure.",
              "**Underlayment:** synthetic underlayment installed across the full deck, replacing the older felt paper that had dried out and torn in several areas.",
              "**Flashing:** new step flashing at wall intersections, new valley metal, and new pipe boots at every roof penetration.",
              "**Ventilation:** existing roof vents checked and supplemented where attic ventilation was found to be inadequate, since poor ventilation shortens shingle life regardless of the shingle brand.",
            ],
          },
          {
            type: "h3", text: "Why CertainTeed" },
          {
            type: "p",
            text: "CertainTeed architectural shingles carry algae-resistance and wind warranties that fit well with San Diego's mix of sun exposure and occasional coastal wind events. For a property manager, the manufacturer-backed warranty also matters because it transfers with documentation the ownership group can keep on file for future tenants or a sale.",
          },
          {
            type: "p",
            text: "Full detail on shingle options and what a tear-off replacement includes is on our [roof replacement](/services/roof-replacement) page.",
          },
        ],
      },
      {
        id: "why-coat-instead-of-replace",
        h2: "Why coat the flat torch-down roof instead of replacing it too?",
        blocks: [
          {
            type: "p",
            text: "The flat roof was not at the end of its life, it just needed protection before it got there. Torch-down modified bitumen roofs typically last 15 to 25 years, and this one was still within that range structurally, with a granulated cap sheet that had started to crack and blister under UV exposure but had not failed.",
          },
          {
            type: "p",
            text: "A silicone roof coating system is designed for exactly this situation: a roof with usable life left where the surface has started to age but the structure underneath is sound. Instead of tearing off a membrane that still had years in it, we cleaned the surface, cut out and patched the cracked and blistered sections with reinforced flashing, then applied a silicone coating system across the entire flat roof.",
          },
          {
            type: "ol",
            items: [
              "Pressure wash and clean the entire roof surface to remove debris and old coating residue.",
              "Cut out blistered and cracked sections of the cap sheet and patch with new modified bitumen and reinforcing fabric.",
              "Prime the surface where needed for coating adhesion.",
              "Apply a base coat of silicone at manufacturer-specified thickness, followed by a topcoat, building total dry film thickness to the level required for the warranty.",
              "Inspect drains and re-flash any that showed wear.",
            ],
          },
          {
            type: "callout",
            title: "What a silicone coating actually buys you",
            text: "A quality silicone roof coating system can add 10 to 15 years of service life to an existing flat roof at a fraction of tear-off and replacement cost, provided the underlying structure is sound and the surface prep is done correctly before the coating goes on.",
          },
        ],
        image: {
          src: singlePlyRoofing.url,
          alt: "Silicone roof coating applied over an existing torch-down flat roof on a San Diego property",
          caption: "The existing torch-down roof after patching and a full silicone coating application, extending its service life.",
        },
      },
      {
        id: "coordinating-two-scopes-one-schedule",
        h2: "How do you run a shingle replacement and a coating job on the same schedule?",
        blocks: [
          {
            type: "p",
            text: "For a property manager, the value of combining these scopes was not just cost. It was having one project manager, one crew mobilization, and one closeout package instead of coordinating two separate roofing contractors on two separate timelines with two separate warranties to track.",
          },
          {
            type: "p",
            text: "We staged the shingle tear-off and replacement first since it required dumpster access and more disruptive work near the units, then moved to the flat roof coating once the sloped roof crew wrapped, using the same site logistics and the same point of contact for tenant notices.",
          },
          {
            type: "p",
            text: "If you are managing a property with mixed roof types, our [commercial roofing](/services/commercial-roofing) team scopes these combined projects regularly, and our [areas served](/areas-served) page covers where we operate across San Diego and the broader region.",
          },
        ],
      },
      {
        id: "cost-and-timeline",
        h2: "What did the project cost and how long did it take?",
        blocks: [
          {
            type: "p",
            text: "Shingle tear-offs and replacements on multi-family sloped sections in San Diego typically run in the range of $13,000 to $30,000 depending on square footage, pitch, and the amount of decking that needs replacing. Silicone coating systems on existing flat roofs generally run $4 to $8 per square foot depending on the amount of surface repair required before coating.",
          },
          {
            type: "p",
            text: "This combined project took a little over two weeks from mobilization to final inspection, with the shingle side taking the bulk of that time given the tear-off and decking work involved.",
          },
          {
            type: "p",
            text: "According to guidance from the [National Roofing Contractors Association](https://www.nrca.net/), coordinating multiple roof sections under a single scope and schedule is one of the most effective ways property owners control both cost and tenant disruption on mixed-roof buildings, which matched our experience on this job closely.",
          },
        ],
        image: {
          src: blogHero5.url,
          alt: "Finished multi-family property roof after CertainTeed shingle replacement and flat roof silicone coating in San Diego",
          caption: "Both roof sections finished within the same schedule, ready for the next inspection cycle.",
        },
      },
      {
        id: "what-good-life-got",
        h2: "What does this kind of project mean for a property owner long term?",
        blocks: [
          {
            type: "p",
            text: "Good Life Property Management ended up with a fully replaced, warrantied shingle roof on the main structure and a flat roof with a fresh coating that resets its clock for another decade or more, all documented under one project file. That documentation matters when a roof section eventually does need attention again, since the coating and shingle warranties both have specific maintenance and inspection requirements to stay valid.",
          },
          {
            type: "p",
            text: "For any property manager weighing a similar decision between full replacement and a coating system on a flat roof, the honest answer depends on what shape the substrate is in underneath the surface. That is worth an actual inspection rather than a guess, which you can schedule through our [contact](/contact) page.",
          },
        ],
      },
    ],
    faqs: [
      {
        q: "How do you decide between a flat roof coating and full replacement?",
        a: "It comes down to the condition of the membrane and the decking underneath it. If the structure is sound and the surface damage is limited to cracking or blistering, a coating system can add 10 to 15 years of life at a fraction of replacement cost. If the membrane has failed or water has reached the decking, replacement is the right call.",
      },
      {
        q: "How long does a silicone roof coating last?",
        a: "A properly applied silicone coating system typically lasts 10 to 15 years before it needs recoating, depending on foot traffic, ponding water, and UV exposure.",
      },
      {
        q: "Why choose CertainTeed shingles for a multi-family property?",
        a: "CertainTeed architectural shingles carry strong wind and algae-resistance warranties and perform well under San Diego's UV exposure, which matters for a property manager who needs a roof to hold up with minimal maintenance calls.",
      },
      {
        q: "Can two different roofing scopes be done on the same project timeline?",
        a: "Yes. Coordinating a shingle replacement and a flat roof coating under one mobilization is common on properties with mixed roof types, and it reduces cost and disruption compared with hiring two separate contractors.",
      },
      {
        q: "Does a roof coating count as a full roof warranty?",
        a: "Coating manufacturers typically offer their own warranty on the coating system itself, separate from any original roof membrane warranty, and it depends on proper surface prep and application thickness being documented at installation.",
      },
    ],
    cta: {
      title: "Managing a property with more than one roof type?",
      text: "We scope shingle, flat, and tile sections together so you get one schedule, one crew, and one clear number. Reach out and we will assess every roof section on your property.",
    },
    ctaType: "roofing",
  },
  {
    slug: "cuyamaca-park-synthetic-tile-roof",
    title: "Synthetic Tile Roof at Cuyamaca Rancho Park | McKay",
    h1: "Why did Cuyamaca Rancho State Park choose a synthetic tile roof for its mountain buildings?",
    metaDescription:
      "A synthetic tile roofing installation at Cuyamaca Rancho State Park, built for fire resistance and mountain weather. Materials, wind and fire ratings, and installation details.",
    keywords:
      "synthetic tile roof, Cuyamaca Rancho State Park, fire resistant roofing, mountain roof installation, Class A roof, composite tile roofing San Diego",
    category: "Projects",
    datePublished: "2026-02-17",
    dateModified: "2026-09-09",
    readMinutes: 7,
    hero: {
      src: blogHero6.url,
      alt: "Completed synthetic tile roof installation on a building at Cuyamaca Rancho State Park in mountain terrain",
      caption: "The finished synthetic tile roof at Cuyamaca Rancho State Park, built to handle fire risk and mountain weather.",
    },
    intro: [
      "Cuyamaca Rancho State Park sits up in the Cuyamaca Mountains east of San Diego, at elevation, in one of the most fire-exposed landscapes in the county. The 2003 Cedar Fire burned through most of the park, and anything built or rebuilt up there since has had to answer a harder question than most residential roofs ever face: will this roof survive an actual wildfire event, not just look good in a brochure.",
      "When it came time to reroof several of the park's structures, the project called for a roofing material that could deliver the look and profile of traditional tile without the weight, cost, and breakage risk of real clay or concrete tile at that elevation and in that terrain. We installed a synthetic tile roofing system, engineered composite tile built specifically for high fire risk and rough mountain weather conditions.",
      "This project is a good example of choosing a roofing material for the actual environment it has to survive in, not just for appearance. Here is what went into the decision, the materials used, and the installation details that make a synthetic tile roof perform the way it is rated to in a place like Cuyamaca.",
    ],
    tldr: [
      "The project used a synthetic composite tile system rather than natural clay or concrete tile, chosen for its lighter weight, impact resistance, and Class A fire rating.",
      "Synthetic tile at this site was installed over a fire-resistant underlayment system rated for the wildfire exposure common in the Cuyamaca Mountains.",
      "The lighter weight of synthetic tile reduced structural load concerns on the existing framing compared with what a full concrete tile reroof would have required.",
      "Mountain conditions, elevation, temperature swings, wind, and wildfire exposure drove nearly every material decision on this job.",
    ],
    sections: [
      {
        id: "why-this-location-matters",
        h2: "Why does a roof at Cuyamaca Rancho State Park need different materials than a roof in the city?",
        blocks: [
          {
            type: "p",
            text: "Cuyamaca sits at roughly 4,000 to 5,000 feet of elevation in dense forest and chaparral terrain that has burned before and will burn again. State park structures in that kind of setting are built under stricter fire and wind exposure expectations than a typical residential reroof closer to the coast.",
          },
          {
            type: "p",
            text: "Temperature swings at elevation are also more extreme than in San Diego proper: hot, dry summers and genuinely cold winters with occasional snow. A roofing material has to handle thermal expansion and contraction across that range without cracking, on top of surviving wind-driven embers in a wildfire scenario.",
          },
          {
            type: "p",
            text: "The [California Department of Forestry and Fire Protection (CAL FIRE)](https://www.fire.ca.gov/) designates most of this region as a Very High Fire Hazard Severity Zone, which is part of what drove the material selection process for this reroof.",
          },
        ],
        image: {
          src: singlePlyRoofing.url,
          alt: "Mountain terrain surrounding Cuyamaca Rancho State Park with forest and chaparral fire exposure",
          caption: "Dense chaparral and forest terrain around the park drove the fire-resistance requirements for this roof.",
        },
      },
      {
        id: "why-synthetic-over-real-tile",
        h2: "Why choose synthetic tile instead of clay or concrete tile?",
        blocks: [
          {
            type: "p",
            text: "Real clay and concrete tile have a long track record and can absolutely be fire-resistant roofing options, but they come with tradeoffs that mattered on this specific project. Concrete tile is heavy, often 900 to 1,100 pounds per square, which can require structural reinforcement on older framing. Both clay and concrete tile are also brittle under impact, and foot traffic or falling branches in a forested setting can crack individual tiles over time.",
          },
          {
            type: "ul",
            items: [
              "**Weight:** synthetic composite tile runs roughly 250 to 450 pounds per square, a fraction of concrete tile, which reduced structural load concerns on the existing roof framing.",
              "**Impact resistance:** synthetic tile flexes rather than cracks under impact from debris, an important factor under tree cover.",
              "**Fire rating:** the synthetic tile system installed carries a Class A fire rating, the highest rating available for roof coverings, matching what natural tile can achieve but without the weight penalty.",
              "**Freeze-thaw performance:** synthetic composite material handles the elevation's occasional freeze cycles better than some clay products, which can be prone to spalling if moisture gets into the tile body and freezes.",
            ],
          },
          {
            type: "p",
            text: "More on how we evaluate tile options for different building types is on our [tile roofing](/services/tile-roofing) page.",
          },
        ],
      },
      {
        id: "the-underlayment-system",
        h2: "What underlayment system goes under a fire-rated tile roof?",
        blocks: [
          {
            type: "p",
            text: "The tile itself is only part of a fire-rated roof assembly. What sits underneath it does most of the work in a real wildfire event, since embers and radiant heat can find gaps at eaves, ridges, and valleys even on a tile roof that looks solid from the ground.",
          },
          {
            type: "h3", text: "Assembly components used on this project" },
          {
            type: "ol",
            items: [
              "A fire-resistant self-adhered underlayment installed across the full deck, rated to work as part of a Class A assembly with the synthetic tile above it.",
              "Battens installed to create proper drainage and ventilation channels beneath the tile, sized for the specific synthetic tile profile.",
              "Ember-resistant venting details at ridge and eave lines, since open venting is one of the most common ways embers get into an attic space during a wildfire.",
              "Metal flashing at all valleys, walls, and penetrations, avoiding combustible flashing materials given the site's fire exposure.",
            ],
          },
          {
            type: "callout",
            title: "The assembly matters more than the material alone",
            text: "A Class A tile sitting over an unrated underlayment with open eave vents is not a Class A roof system anymore. Fire rating applies to the complete assembly, not just the visible surface, which is why the underlayment and venting details on this job got as much attention as the tile itself.",
          },
        ],
        image: {
          src: singlePlyRoofing.url,
          alt: "Fire-resistant underlayment and battens installed before synthetic tile placement at a mountain park building",
          caption: "Fire-resistant underlayment and battens went down before a single tile was set.",
        },
      },
      {
        id: "installation-in-mountain-conditions",
        h2: "What made installing a tile roof at this elevation different from a coastal job?",
        blocks: [
          {
            type: "p",
            text: "Access and weather windows were the two biggest practical differences. Materials had to be staged and hauled up mountain roads rather than delivered directly to a driveway, and crews worked around shorter weather windows given seasonal rain and the site's elevation.",
          },
          {
            type: "p",
            text: "Wind exposure at elevation also called for tighter fastening schedules than we would use on a similar roof closer to sea level. The synthetic tile system was installed with a fastening pattern and nail schedule specified for the site's wind zone, with additional attachment at ridges, hips, and roof edges where uplift forces concentrate.",
          },
          {
            type: "p",
            text: "This kind of site-specific detailing is standard on our public and institutional roofing work, and it is the same process our [commercial roofing](/services/commercial-roofing) team runs on any building with elevated fire or wind exposure requirements.",
          },
        ],
      },
      {
        id: "results-and-durability",
        h2: "How long is this roof expected to last, and what does it protect?",
        blocks: [
          {
            type: "p",
            text: "Synthetic composite tile systems like the one installed here typically carry manufacturer warranties in the 40 to 50 year range, with Class A fire ratings that do not degrade over that lifespan the way some coatings or treatments can. That is a meaningful number for a state park building where reroofing crews and material access are far more expensive to mobilize than on a typical residential job.",
          },
          {
            type: "p",
            text: "Beyond the roofing material itself, projects like this protect public buildings and, by extension, park staff and visitors who rely on those structures during wildfire season. A roof that survives an ember storm is one less ignition point in a fire that could otherwise spread from structure to structure.",
          },
          {
            type: "p",
            text: "The [Insurance Institute for Business & Home Safety](https://ibhs.org/) publishes independent research on which roofing assemblies actually perform in wildfire conditions, and the assembly used on this project aligns closely with their recommended approach: Class A covering, ember-resistant venting, and non-combustible flashing working together rather than any single material doing the job alone.",
          },
        ],
        image: {
          src: blogHero6.url,
          alt: "Finished synthetic tile roof on a Cuyamaca Rancho State Park structure surrounded by forest",
          caption: "A finished, fire-rated roof assembly built to hold up in one of San Diego County's highest fire-risk zones.",
        },
      },
      {
        id: "what-this-means-for-other-properties",
        h2: "Does this apply to homes and buildings outside a state park?",
        blocks: [
          {
            type: "p",
            text: "Yes, and this is really the point of walking through a project like this in detail. Any property in San Diego's backcountry or foothill communities, places like Alpine, Julian, Ramona, or the eastern edges of El Cajon, sits in similar fire terrain to Cuyamaca, even if the fire hazard maps do not use the exact same zone designation.",
          },
          {
            type: "p",
            text: "The same logic applies: a Class A fire-rated tile assembly, correctly detailed underlayment and venting, and non-combustible flashing are available for homes at a much smaller scale than a state park building. If you are in one of these areas and weighing roofing materials, our [areas served](/areas-served) page covers where our crews work, and our [contact](/contact) page is the fastest way to get a fire-rated roofing assessment scheduled.",
          },
        ],
      },
    ],
    faqs: [
      {
        q: "Is synthetic tile actually fire-resistant, or just tile-looking?",
        a: "Quality synthetic composite tile systems carry an independently tested Class A fire rating, the highest available for roof coverings, when installed as part of a complete assembly with a compatible fire-resistant underlayment.",
      },
      {
        q: "How does synthetic tile compare in weight to concrete tile?",
        a: "Synthetic composite tile typically weighs 250 to 450 pounds per square, compared with 900 to 1,100 pounds per square for concrete tile, which can matter significantly for older or lighter framed structures.",
      },
      {
        q: "Can synthetic tile handle snow and freeze cycles at elevation?",
        a: "Yes. Composite synthetic tile does not absorb moisture the way some clay tile products can, which reduces the risk of freeze-thaw cracking at higher, colder elevations like Cuyamaca.",
      },
      {
        q: "What makes a roof assembly truly fire-rated, not just the tile itself?",
        a: "The full assembly matters: the tile, the underlayment, the venting at eaves and ridges, and the flashing materials all have to work together. A Class A tile over unrated underlayment with open venting is not a true Class A system.",
      },
      {
        q: "How long does a synthetic tile roof last?",
        a: "Most manufacturer warranties on quality synthetic composite tile systems run in the 40 to 50 year range, comparable to natural tile but at a fraction of the installed weight.",
      },
      {
        q: "Do you install this kind of fire-rated roofing on residential homes too?",
        a: "Yes. The same Class A assembly approach used at Cuyamaca Rancho State Park applies to homes in San Diego's backcountry and foothill communities that face similar wildfire exposure.",
      },
    ],
    cta: {
      title: "In a high fire risk area and thinking about your roof?",
      text: "We install Class A fire-rated roofing assemblies, tile, synthetic, and metal, built for San Diego's backcountry and foothill fire zones. Get an assessment before fire season, not during it.",
    },
    ctaType: "roofing",
  },
];
